/* eslint-disable function-paren-newline */
import React, { FC, useEffect, useState } from 'react';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import CustomCheckbox from 'components/custom-checkbox/CustomCheckbox';
import CustomModal from 'components/custom-modal/CustomModal';
import {
  CleverTapEventsLegalReacceptance,
  LegalAgreementAcceptancePayload,
  LegalReacceptanceContent
} from 'containers/legal-reacceptance/legalReacceptance.model';
import { DeviceFingerprintService } from 'utils/device-fingerprint';
import { getClientIP } from 'utils/apiUtils';
import { CheckboxTickIcon } from 'assets/icons';
import { logAnalyticEvent } from 'utils/analytics';
import DateUtils from 'utils/dateUtils';

interface LegalReacceptanceModalProps {
  open: boolean;
  content: LegalReacceptanceContent;
  onConfirm: (payload: LegalAgreementAcceptancePayload) => void | Promise<void>;
  isConfirming?: boolean;
}

const buildDeviceInfoFromFingerprint = (
  fingerprint: Awaited<ReturnType<typeof DeviceFingerprintService.collect>>
): Record<string, string> => {
  const { signals } = fingerprint;
  const [screenWidth, screenHeight] = signals.screenResolution;
  return {
    'User-Agent': signals.userAgent,
    Platform: signals.platform,
    Language: signals.language,
    Timezone: signals.timezone,
    TimezoneOffset: String(signals.timezoneOffset),
    ScreenResolution: `${screenWidth}x${screenHeight}`,
    DevicePixelRatio: String(signals.devicePixelRatio),
    HardwareConcurrency: String(signals.hardwareConcurrency),
    DeviceMemory: String(signals.deviceMemory ?? 0),
    MaxTouchPoints: String(signals.maxTouchPoints)
  };
};

const LegalReacceptanceModal: FC<LegalReacceptanceModalProps> = ({ open, content, onConfirm, isConfirming }) => {
  const checkboxes = content.checkboxes ?? [];
  const [checkedState, setCheckedState] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(checkboxes.map((cb, i) => [cb.id ?? `checkbox-${i}`, false]))
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    logAnalyticEvent(CleverTapEventsLegalReacceptance.webLegalReacceptanceModalView);
  }, [open]);

  const allRequiredChecked = checkboxes
    .map((cb, i) => ({ cb, id: cb.id ?? `checkbox-${i}` }))
    .filter(({ cb }) => cb.required ?? true)
    .every(({ id }) => checkedState[id]);

  const toggleCheckbox = (id: string) => {
    setCheckedState(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleConfirm = async () => {
    if (!content.agreementVersions) return;
    setIsSubmitting(true);
    try {
      const [fingerprint, ip] = await Promise.all([DeviceFingerprintService.collect(), getClientIP()]);
      const payload: LegalAgreementAcceptancePayload = {
        eventType: 'RE-ACCEPTANCE',
        legalAgreementsAcceptanceData: {
          ip,
          deviceInfo: buildDeviceInfoFromFingerprint(fingerprint),
          agreementVersions: content.agreementVersions,
          acceptedAt: DateUtils.getCurrentUTCTimestamp()
        }
      };
      await onConfirm(payload);
      logAnalyticEvent(CleverTapEventsLegalReacceptance.webLegalReacceptanceAcceptClick);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CustomModal open={open} enableOutsideClick={false} width="w-full md:w-[450px]" height="h-auto md:h-[660px]">
      <div className="flex h-full flex-col gap-5 p-8">
        <p className="text-xl font-bold text-primaryText">{content.title}</p>
        <p className="text-sbase font-semibold text-primaryText">{content.body}</p>
        <div className="flex flex-col gap-3">
          {checkboxes.map((checkbox, index) => {
            const checkboxId = checkbox.id ?? `checkbox-${index}`;
            return (
              <div key={checkboxId} className="flex items-start gap-3 rounded-md border border-borderGray p-4">
                <CustomCheckbox
                  id={checkboxId}
                  isChecked={!!checkedState[checkboxId]}
                  onClick={() => toggleCheckbox(checkboxId)}
                  className="mt-0.5 shrink-0">
                  {checkedState[checkboxId] && <CheckboxTickIcon />}
                </CustomCheckbox>
                <span className="text-sbase font-semibold text-primaryText">
                  {(checkbox.segments ?? []).map((segment, idx) =>
                    segment.type === 'link' ? (
                      <a
                        key={idx}
                        className="text-secondary"
                        href={segment.url}
                        target="_blank"
                        rel="noopener noreferrer">
                        {segment.text}
                      </a>
                    ) : (
                      <span key={idx}>{segment.text}</span>
                    )
                  )}
                </span>
              </div>
            );
          })}
        </div>
        <CustomButton
          type={ButtonType.PRIMARY}
          onClick={handleConfirm}
          disabled={!allRequiredChecked || isConfirming || isSubmitting || !content.agreementVersions}
          id="legal-reacceptance-confirm-btn"
          className="mt-auto h-12 w-full self-end "
          childrenContainerStyles="text-17px font-semibold"
          isLoading={isConfirming || isSubmitting}>
          {content.confirmButtonText}
        </CustomButton>
      </div>
    </CustomModal>
  );
};

export default LegalReacceptanceModal;
